import React, {useEffect, useMemo, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetDistanceAndCostQuery } from "../../../Store/Builder/schedule.api";
import showToast from "../../../Global/Hooks/toast";
import { setLoader } from "../../../Store/Slice/techsbookSlice";
import { useParams } from "react-router-dom";
import moment from "moment";

export default function Pricing({ setTotalPriceValue }) {
  const { title: jobTitle } = useParams();
  const dispatch = useDispatch();
  const line_items = useSelector((state) => state.rootReducer.scheduleSlice.line_items);
  const accountData = useSelector((state) => state.rootReducer.techsbookSlice.userData);
  const [getDistanceAndCost] = useLazyGetDistanceAndCostQuery();
  const techRadius = useSelector((state) => state.rootReducer.scheduleSlice.radius);
  const scheduleTech = useSelector((state) => state.rootReducer.scheduleSlice.tech);
  const duration = useSelector((state) => state.rootReducer.scheduleSlice.total_time);
  const { lat, lon, job_datetime: date } = useSelector((state) => state.rootReducer.scheduleSlice.sendData);
  const { schedule_step: scheduleStep, tech: techData } = useSelector((state) => state.rootReducer.scheduleSlice);
  const sendData = useSelector((state) => state.rootReducer.scheduleSlice.sendData);
  const { activeJob } = useSelector((state) => state.rootReducer.jobsSlice);
  const creditValue = useSelector((state) => state.rootReducer.scheduleSlice.sendData.credit_to_use);
  const [showInfo, setShowInfo] = useState(false);

  const [distanceAndCost, setDistanceAndCost] = useState(null);
  const [isReschedule, setIsReschedule] = useState(false);

  const isDatePast = useMemo(() => {
    if (!activeJob?.job_datetime) return false;
    return moment(activeJob.job_datetime).isBefore(new Date());
  }, [activeJob?.job_datetime]);

  const calculateLineItemsTotal = (items) => {
    return items?.reduce((acc, item) => acc + (+item?.price || 0), 0);
  };

  const calculateDistanceFee = (data) => {
    if (!data) return 0;
    let { distance, no_charge_distance, millage_fee } = data;
    distance = calculateMileageQty(data);
    return Math.max(distance - no_charge_distance, 0) * millage_fee;
  };

  const calculateMileageQty = (data) => {
    if (!data) return 0;
    let { distance } = data;
    if(isReschedule && !(isDateIn24Hrs || isDatePast)){
      distance = Math.max(distance - sendData.millage_round_trip_distance, 0);
    }
    return distance;
  };

  const calculateMileageDiscount = (data) => {
    if (!data) return 0;
    let { distance, no_charge_distance, millage_fee } = data;
    distance = calculateMileageQty(data);
    return Math.min(millage_fee * no_charge_distance, distance * millage_fee);
  };

  const calculateTotalUS = (itemsTotal, distanceFee) => {
    if(!isReschedule){
      return itemsTotal + distanceFee;
    }
    return ((isReschedule && (isDateIn24Hrs || isDatePast) && itemsTotal) + distanceFee);
  };

  const formatPrice = (value) => {
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchTechDistanceAndCost = () => {
    if (
        scheduleTech?.technician_id?.id &&
        duration &&
        lat &&
        lon &&
        date &&
        techRadius
    ) {
      const params = {
        id: scheduleTech.technician_id.id,
        duration,
        lat,
        lon,
        date,
        radius: techRadius,
      };

      dispatch(setLoader(true));
      getDistanceAndCost(params)
          .unwrap()
          .then((res) => {
            dispatch(setLoader(false));
            setDistanceAndCost(res);
          })
          .catch((err) => {
            dispatch(setLoader(false));
            showToast(err?.data?.message ?? "Error", "error");
          });
    }
  };

  // ────────────────────────────────────────────────────────

  useEffect(() => {
    setIsReschedule(Boolean(jobTitle?.length));
  }, [jobTitle]);

  useEffect(() => {
    if (scheduleStep === 4 && isReschedule) fetchTechDistanceAndCost();
  }, [scheduleStep, isReschedule]);

  useEffect(() => {
    fetchTechDistanceAndCost();
  }, [scheduleTech?.technician_id?.id]);

  useEffect(() => {
    if (distanceAndCost) {
      const lineItemsTotal = calculateLineItemsTotal(line_items);
      const distanceFee = calculateDistanceFee(distanceAndCost);
      const totalPrice = calculateTotalUS(lineItemsTotal, distanceFee).toFixed(2);
      if (!isNaN(totalPrice)) {
        setTotalPriceValue(Number(totalPrice));
      }
    }
  }, [line_items, distanceAndCost, setTotalPriceValue]);

  useEffect(() => {
      const total = formatPrice(calculateTotalUS(calculateLineItemsTotal(line_items), calculateDistanceFee(distanceAndCost)));
      if(total <= 0){

      }
  }, [creditValue]);

  const scheduledDate = new Date(activeJob?.job_datetime).getTime();
  const currentDate = new Date(new Date().toLocaleString("en-US", { timeZone: activeJob?.job_timezone })).getTime();


  const diffInMs = scheduledDate - currentDate;
  const isDateIn24Hrs = useMemo(() => {
    return (diffInMs > 0 && diffInMs <= 24 * 60 * 60 * 1000);
  }, [activeJob?.job_datetime]);

    const rawTotal = calculateTotalUS(
        calculateLineItemsTotal(line_items),
        calculateDistanceFee(distanceAndCost)
    );

    const finalTotal = rawTotal - creditValue;

    useEffect(() => {
        if (finalTotal <= 0) {
            setShowInfo(true);
        } else {
            setShowInfo(false);
        }
    }, [finalTotal]);

  return (
      <div className="payment__pricing">
          {
              line_items?.length > 0 && (
                  <div className="payment__items">
                      <div className="payment__pricing-title">Pricing overview</div>
                      {line_items?.map((item, index) => (
                          <p key={index}>
                              <span>{item?.data?.label || item?.product?.label} x{item?.quantity}</span>
                              <span>{accountData?.country_currency_sign}{formatPrice(parseFloat(item?.price) || 0)}</span>
                          </p>
                      ))}
                      {
                          distanceAndCost && <>
                              <p>
                                  <span>Mileage (Round Trip) x{formatPrice(calculateMileageQty(distanceAndCost))}</span>
                                  <span>{accountData?.country_currency_sign}{formatPrice(calculateMileageQty(distanceAndCost) * distanceAndCost?.millage_fee)}</span>
                              </p>
                              <p>
                                  <span>Mileage discount (max {distanceAndCost?.no_charge_distance}) x{distanceAndCost?.no_charge_distance}</span>
                                  <span>-{accountData?.country_currency_sign}{formatPrice(calculateMileageDiscount(distanceAndCost))}</span>
                              </p>
                          </>
                      }
                      {
                          creditValue > 0 && <p style={{color: '#3772FF'}}>
                              <span>Tokens applied</span>
                              <span>-{accountData?.country_currency_sign}{creditValue}</span>
                          </p>
                      }
                      <p style={{paddingTop: '12px', borderTop: '1px solid #eaeaea'}}>
                          <span>
                              <strong>Total US</strong>
                          </span>
                          <span>
                              <strong>
                                  <strong>
                                      {accountData?.country_currency_sign}
                                      {finalTotal > 0 ? formatPrice(finalTotal) : "0"}
                                  </strong>
                              </strong>
                          </span>
                      </p>
                  </div>
              )
          }
          {
              showInfo && (
                  <div className="payment__info">
                      <img src="/assets/icons/card-info.svg" alt=""/>
                      <p>Please select a card to process the payment. If your tokens cover the full amount, your card will not be charged.</p>
                  </div>
              )
          }

        {/*<table className="payment__table">*/}
        {/*  <thead>*/}
        {/*  <tr>*/}
        {/*    <th></th>*/}
        {/*    <th>Qty</th>*/}
        {/*    <th>Rate</th>*/}
        {/*    <th>Total</th>*/}
        {/*  </tr>*/}
        {/*  </thead>*/}
        {/*  <tbody>*/}
        {/*  {*/}
        {/*    !isReschedule && line_items?.length > 0 &&*/}
        {/*      line_items?.map((item, index) => (*/}
        {/*          <tr key={index}>*/}
        {/*            <td>{item?.data?.label || item?.product?.label}</td>*/}
        {/*            <td>{item?.quantity}</td>*/}
        {/*            <td>*/}
        {/*              {accountData?.country_currency_sign}*/}
        {/*              {item?.type_of_service?.price || item?.price}*/}
        {/*            </td>*/}
        {/*            <td>*/}
        {/*              {accountData?.country_currency_sign}*/}
        {/*              {parseFloat(item?.price).toLocaleString()}*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*      ))*/}
        {/*  }*/}
        {/*  {isReschedule && (isDateIn24Hrs || isDatePast) && line_items?.length > 0 &&*/}
        {/*      line_items?.map((item, index) => (*/}
        {/*          <tr key={index}>*/}
        {/*            <td>{item?.data?.label || item?.product?.label}</td>*/}
        {/*            <td>{item?.quantity}</td>*/}
        {/*            <td>*/}
        {/*              {accountData?.country_currency_sign}*/}
        {/*              {item?.type_of_service?.price || item?.price}*/}
        {/*            </td>*/}
        {/*            <td>*/}
        {/*              {accountData?.country_currency_sign}*/}
        {/*              {parseFloat(item?.price).toLocaleString()}*/}
        {/*            </td>*/}
        {/*          </tr>*/}
        {/*      ))}*/}

        {/*  {distanceAndCost && (*/}
        {/*      <>*/}
        {/*        <tr>*/}
        {/*          <td>Mileage (Round Trip)</td>*/}
        {/*          <td>{formatPrice(calculateMileageQty(distanceAndCost))}</td>*/}
        {/*          <td>{accountData?.country_currency_sign}{distanceAndCost?.millage_fee}</td>*/}
        {/*          <td>{accountData?.country_currency_sign}{formatPrice(calculateMileageQty(distanceAndCost) * distanceAndCost?.millage_fee)}</td>*/}
        {/*        </tr>*/}

        {/*        <tr>*/}
        {/*          <td>Mileage discount <br />(max {distanceAndCost?.no_charge_distance})</td>*/}
        {/*          <td>{distanceAndCost?.no_charge_distance}</td>*/}
        {/*          <td>{accountData?.country_currency_sign}{distanceAndCost?.millage_fee}</td>*/}
        {/*          <td>*/}
        {/*            <b>-</b>*/}
        {/*            {accountData?.country_currency_sign}*/}
        {/*            {formatPrice(calculateMileageDiscount(distanceAndCost))}*/}
        {/*          </td>*/}
        {/*        </tr>*/}
        {/*        <tr style={{ border: "none" }}>*/}
        {/*          <td>*/}
        {/*            <b>Total US</b>*/}
        {/*          </td>*/}
        {/*          <td></td>*/}
        {/*          <td></td>*/}
        {/*          <td>*/}
        {/*            <b>*/}
        {/*              {accountData?.country_currency_sign}*/}
        {/*              {formatPrice(calculateTotalUS(calculateLineItemsTotal(line_items), calculateDistanceFee(distanceAndCost)))}*/}
        {/*            </b>*/}
        {/*          </td>*/}
        {/*        </tr>*/}
        {/*      </>*/}
        {/*  )}*/}
        {/*  </tbody>*/}
        {/*</table>*/}
      </div>
  );
}
